import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  Box,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  NotificationsActive as FollowUpIcon,
  NotificationsOff as FollowUpOffIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  activeIcon: {
    color: "#10b981", // Green when active
  },
  inactiveIcon: {
    color: theme.palette.text.secondary,
  },
  dialogContent: {
    minWidth: 350,
    // On a 360px phone the maxWidth="xs" dialog paper is ~296px, so a hard
    // 350px content box overflows the card and the screen edge. Let it
    // shrink to the paper width on phones.
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
  },
  templateInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f8fafc",
    borderRadius: 8,
    fontSize: "0.85rem",
  },
  statusBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
}));

// Parse template body to find all {{1}}, {{2}}, etc.
const parseTemplateParameters = (bodyText) => {
  if (!bodyText) return [];
  const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
  const uniqueParams = [...new Set(matches)];
  return uniqueParams
    .map((m) => m.replace(/[{}]/g, ""))
    .sort((a, b) => parseInt(a) - parseInt(b));
};

const FollowUpToggle = ({ ticket }) => {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [followUpEnabled, setFollowUpEnabled] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [parameters, setParameters] = useState({}); // { "1": "Name", "2": "Topic" }
  const [templateParams, setTemplateParams] = useState([]); // ["1", "2"]
  const [delayMinutes, setDelayMinutes] = useState(1440); // Default 24h

  const fetchFollowUpStatus = useCallback(async () => {
    if (!ticket?.id) return;
    try {
      const { data } = await api.get(`/follow-up/${ticket.id}`);
      setFollowUpEnabled(data.followUpEnabled);
      setSelectedTemplateId(data.followUpTemplateId || "");
      setCurrentTemplate(data.followUpTemplate);
      setContactData(data.contact);
      setDelayMinutes(data.followUpDelayMinutes || 1440);

      // Load saved parameters or initialize with customer name
      if (
        data.followUpParameters &&
        Object.keys(data.followUpParameters).length > 0
      ) {
        setParameters(data.followUpParameters);
      } else if (data.contact?.name) {
        setParameters({ 1: data.contact.name });
      } else {
        setParameters({});
      }

      // Parse template params if template exists
      if (data.followUpTemplate?.bodyText) {
        setTemplateParams(
          parseTemplateParameters(data.followUpTemplate.bodyText)
        );
      }
    } catch (err) {
      console.error("Error fetching follow-up status:", err);
    }
  }, [ticket?.id]);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp-templates", {
        params: {
          status: "APPROVED",
          activeOnly: true,
          deduplicate: true,
        },
      });
      setTemplates(data);
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  }, []);

  // Fetch current follow-up status when dialog opens
  useEffect(() => {
    if (open && ticket?.id) {
      fetchFollowUpStatus();
      fetchTemplates();
    }
  }, [open, ticket?.id, fetchFollowUpStatus, fetchTemplates]);

  const handleToggleFollowUp = async () => {
    if (!followUpEnabled && !selectedTemplateId) {
      toast.error(i18n.t("followUp.errors.selectTemplate"));
      return;
    }

    setLoading(true);
    try {
      // Only send non-empty parameters
      const nonEmptyParams = Object.fromEntries(
        Object.entries(parameters).filter(([_, v]) => v && v.trim())
      );

      const { data } = await api.post(`/follow-up/${ticket.id}/toggle`, {
        enabled: !followUpEnabled,
        templateId: selectedTemplateId || undefined,
        parameters:
          !followUpEnabled && Object.keys(nonEmptyParams).length > 0
            ? nonEmptyParams
            : undefined,
        delayMinutes: !followUpEnabled ? delayMinutes : undefined,
      });

      setFollowUpEnabled(data.followUpEnabled);
      setCurrentTemplate(
        data.followUpEnabled
          ? templates.find((t) => t.id === selectedTemplateId)
          : null
      );

      toast.success(
        data.followUpEnabled
          ? i18n.t("followUp.success.enabled")
          : i18n.t("followUp.success.disabled")
      );

      if (!data.followUpEnabled) {
        setOpen(false);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Only show for open/pending tickets
  if (!ticket || !["open", "pending"].includes(ticket.status)) {
    return null;
  }

  return (
    <>
      <Tooltip title={i18n.t("followUp.tooltip")}>
        <IconButton onClick={() => setOpen(true)} size="small">
          {ticket.followUpEnabled ? (
            <FollowUpIcon className={classes.activeIcon} />
          ) : (
            <FollowUpOffIcon className={classes.inactiveIcon} />
          )}
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n.t("followUp.dialog.title")}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {i18n.t("followUp.dialog.description")}
          </Typography>

          <Box className={classes.statusBox} style={{ marginTop: 16 }}>
            <Typography variant="body1">
              {i18n.t("followUp.dialog.status")}
            </Typography>
            <Switch
              checked={followUpEnabled}
              onChange={handleToggleFollowUp}
              color="primary"
              disabled={loading || (!followUpEnabled && !selectedTemplateId)}
            />
          </Box>

          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            style={{ marginTop: 8 }}
          >
            <InputLabel>{i18n.t("followUp.dialog.selectTemplate")}</InputLabel>
            <Select
              value={selectedTemplateId}
              onChange={(e) => {
                const templateId = e.target.value;
                setSelectedTemplateId(templateId);

                // Find selected template and parse its parameters
                const template = templates.find((t) => t.id === templateId);
                if (template?.bodyText) {
                  const params = parseTemplateParameters(template.bodyText);
                  setTemplateParams(params);

                  // Auto-fill {{1}} with customer name if not already set
                  if (
                    params.includes("1") &&
                    !parameters["1"] &&
                    contactData?.name
                  ) {
                    setParameters((prev) => ({ ...prev, 1: contactData.name }));
                  }
                } else {
                  setTemplateParams([]);
                }
              }}
              label={i18n.t("followUp.dialog.selectTemplate")}
              disabled={followUpEnabled}
            >
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name} ({template.language})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Delay selector */}
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            style={{ marginTop: 16 }}
          >
            <InputLabel>{i18n.t("followUp.dialog.delay")}</InputLabel>
            <Select
              value={delayMinutes}
              onChange={(e) => setDelayMinutes(e.target.value)}
              label={i18n.t("followUp.dialog.delay")}
              disabled={followUpEnabled}
            >
              <MenuItem value={5}>
                5 {i18n.t("followUp.dialog.minutes")} (
                {i18n.t("followUp.dialog.testMode")})
              </MenuItem>
              <MenuItem value={60}>1 {i18n.t("followUp.dialog.hour")}</MenuItem>
              <MenuItem value={1440}>
                24 {i18n.t("followUp.dialog.hours")}
              </MenuItem>
            </Select>
          </FormControl>

          {/* Dynamic parameter fields based on template */}
          {selectedTemplateId &&
            !followUpEnabled &&
            templateParams.length > 0 && (
              <Box style={{ marginTop: 16 }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginBottom: 8, display: "block" }}
                >
                  {i18n.t("followUp.dialog.fillParameters")}
                </Typography>
                {templateParams.map((paramNum) => (
                  <TextField
                    key={paramNum}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label={`{{${paramNum}}} - ${
                      paramNum === "1"
                        ? i18n.t("followUp.dialog.customerName")
                        : i18n.t("followUp.dialog.parameter") + " " + paramNum
                    }`}
                    value={parameters[paramNum] || ""}
                    onChange={(e) =>
                      setParameters((prev) => ({
                        ...prev,
                        [paramNum]: e.target.value,
                      }))
                    }
                    placeholder={
                      paramNum === "1" ? contactData?.name || "" : ""
                    }
                    style={{ marginTop: 8 }}
                  />
                ))}
              </Box>
            )}

          {/* Show message if template has no parameters */}
          {selectedTemplateId &&
            !followUpEnabled &&
            templateParams.length === 0 && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginTop: 16, display: "block" }}
              >
                {i18n.t("followUp.dialog.noParameters")}
              </Typography>
            )}

          {followUpEnabled && currentTemplate && (
            <Box className={classes.templateInfo}>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("followUp.dialog.activeTemplate")}:
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {currentTemplate.name}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginTop: 4, display: "block" }}
              >
                {delayMinutes === 5
                  ? i18n.t("followUp.dialog.willSendAfter5min")
                  : delayMinutes === 60
                  ? i18n.t("followUp.dialog.willSendAfter1h")
                  : i18n.t("followUp.dialog.willSendAfter24h")}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            {i18n.t("followUp.dialog.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FollowUpToggle;
